import React from "react";
import { FaReceipt, FaCheckCircle, FaClock } from "react-icons/fa";
import useStore from "../../../store/useStore";

const OrderConfirmedPriceBreakdown = ({ order }) => {
  const { cart_meta } = useStore();
  const deliveryOptions = cart_meta?.delivery_options || [];
  const rawDeliveryType = String(order?.delivery_type || "STANDARD").toUpperCase();

  const matchedOpt = deliveryOptions.find(
    (o) => String(o.type).toUpperCase() === rawDeliveryType
  );

  // Always start from the actual fee stored on the order (the ground truth).
  // Only override if the live delivery_options API has an explicit fee for this type.
  let delivery = parseFloat(order?.delivery_fee ?? 0);
  if (matchedOpt && matchedOpt.fee != null) {
    delivery = Number(matchedOpt.fee);
  }

  const subtotal = parseFloat(order?.subtotal ?? 0);
  const discount = parseFloat(order?.discount_amount ?? 0);
  const gst = parseFloat(order?.tax_amount ?? 0);
  const calculatedTotal = subtotal + gst + delivery - discount;
  const grandTotal = calculatedTotal > 0 ? calculatedTotal : parseFloat(order?.total_amount ?? 0);
  const isPaid = ["PAID", "SUCCESS"].includes(String(order?.payment_status || "").toUpperCase());

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm space-y-4">
      {/* Price breakdown */}
      <div>
        <div className="flex items-center gap-2 pb-2 border-b border-gray-100 mb-3">
          <FaReceipt className="h-4 w-4 text-[#046a38]" />
          <h3 className="text-sm font-bold text-gray-900">Price Details</h3>
        </div>

        <div className="space-y-2 text-xs text-gray-600">
          <div className="flex justify-between">
            <span>Items Subtotal</span>
            <span className="font-semibold text-gray-900">₹{subtotal.toFixed(2)}</span>
          </div>

          {discount > 0 && (
            <div className="flex justify-between text-[#046a38] font-medium">
              <span>{order?.coupon_code ? `Coupon savings (${order.coupon_code})` : 'Discount'}</span>
              <span>- ₹{discount.toFixed(2)}</span>
            </div>
          )}

          <div className="flex justify-between">
            <span>GST / Tax Amount</span>
            <span className="font-semibold text-gray-900">₹{gst.toFixed(2)}</span>
          </div>

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1.5">
              <span>Delivery Fee</span>
              {rawDeliveryType === "SCHEDULED" ? (
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                  Scheduled
                </span>
              ) : rawDeliveryType === "EXPRESS" ? (
                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                  Express
                </span>
              ) : (
                <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                  Standard
                </span>
              )}
            </div>
            <span
              className={`font-semibold ${
                delivery === 0 ? "text-[#046a38] font-bold" : "text-gray-900"
              }`}
            >
              {delivery === 0 ? "FREE" : `₹${delivery.toFixed(2)}`}
            </span>
          </div>

          <div className="pt-2 border-t border-gray-100 flex justify-between items-center text-sm font-black text-gray-900">
            <span>Total Amount</span>
            <span className="text-base text-[#046a38]">₹{grandTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Payment method transaction box */}
      <div className="rounded-xl bg-gray-50 border border-gray-200/80 p-3 text-xs space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-gray-500 font-medium">Payment Mode</span>
          <span className="font-bold text-gray-900 uppercase">
            {order?.payment_method || "UPI"}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-500 font-medium">Payment Status</span>
          <span
            className={`inline-flex items-center gap-1 font-bold ${
              isPaid ? "text-[#046a38]" : "text-amber-600"
            }`}
          >
            {isPaid ? (
              <>
                <FaCheckCircle className="h-3 w-3" />
                <span>Paid</span>
              </>
            ) : (
              <>
                <FaClock className="h-3 w-3" />
                <span>Pending / COD</span>
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmedPriceBreakdown;
